import React, { useState, useMemo } from 'react';
import { Modal, Field, Input, Select, Btn, SectionHeader } from '../components/UI';
import { COSTOS_FIJOS, fmtM, fmtDate, todayStr, gcalUrl } from '../utils';
import { showToast } from '../components/UI';

export default function Costos({ store, setCol, setItem }) {
  const { movimientos, costosMes } = store;
  const hoy = todayStr();
  const mes = hoy.slice(0, 7);
  const [payModal, setPayModal] = useState(null);
  const [newCostoName, setNewCostoName] = useState('');
  const [payMedio, setPayMedio] = useState('transferencia');
  const [payFecha, setPayFecha] = useState(hoy);

  // Load or init costos for current month
  const [costos, setCostosLocal] = useState(() => {
    const saved = store.costosMes;
    if (saved && Object.keys(saved).length > 0) return saved;
    const obj = {};
    COSTOS_FIJOS.forEach(n => {
      obj[n] = { nombre: n, monto: 0, fecha: '', esAmortizacion: n === 'Amortizaciones', pagado: false, fechaPago: '', medioPago: '' };
    });
    return obj;
  });

  function updateCosto(nombre, field, value) {
    const updated = { ...costos, [nombre]: { ...costos[nombre], [field]: value } };
    setCostosLocal(updated);
    setCol('costosMes', updated);
  }

  const [payComprobante, setPayComprobante] = useState('');

  function openPay(costo) {
    setPayMedio('transferencia');
    setPayFecha(hoy);
    setPayComprobante(costo.comprobante || '');
    setPayModal(costo);
  }

  function confirmPay() {
    const c = payModal;
    // Register payment as movement
    const movId = 'm' + Date.now();
    setItem('movimientos', movId, {
      id: movId, tipo: 'pago_proveedor', fecha: payFecha,
      hora: new Date().toTimeString().slice(0, 5),
      monto: parseFloat(c.monto) || 0, medio: payMedio, signo: '-',
      desc: `Pago ${c.nombre}`, concepto: c.nombre, autor: 'panel'
    });

    // Mark as paid and set next month date
    const nextMonth = payFecha ? (() => {
      const d = new Date(payFecha);
      d.setMonth(d.getMonth() + 1);
      return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    })() : '';

    const updated = {
      ...costos,
      [c.nombre]: { ...c, pagado: true, fechaPago: payFecha, medioPago: payMedio, fechaSiguiente: nextMonth, comprobante: payComprobante }
    };
    setCostosLocal(updated);
    setCol('costosMes', updated);
    showToast(`${c.nombre} marcado como pagado — próx. ${fmtDate(nextMonth)}`);
    setPayModal(null);
  }

  function resetPago(nombre) {
    const c = costos[nombre];
    // Use fechaSiguiente as new fecha
    const nextFecha = c.fechaSiguiente || '';
    const updated = { ...costos, [nombre]: { ...c, pagado: false, fechaPago: '', medioPago: '', fecha: nextFecha, fechaSiguiente: '' } };
    setCostosLocal(updated);
    setCol('costosMes', updated);
    showToast('Fecha actualizada al mes siguiente');
  }

  function addCosto() {
    const nombre = newCostoName.trim();
    if (!nombre) return;
    if (costos[nombre]) { showToast('Ya existe ese costo'); return; }
    const updated = { ...costos, [nombre]: { nombre, monto: 0, fecha: '', esAmortizacion: false, pagado: false, fechaPago: '', medioPago: '' } };
    setCostosLocal(updated);
    setCol('costosMes', updated);
    setNewCostoName('');
    showToast('Costo agregado');
  }

  function deleteCosto(nombre) {
    const updated = { ...costos };
    delete updated[nombre];
    setCostosLocal(updated);
    setCol('costosMes', updated);
    showToast('Costo eliminado');
  }

  const gastosVarMes = useMemo(() =>
    Object.values(movimientos || {}).filter(m => m.tipo === 'gasto' && m.fecha?.slice(0, 7) === mes),
    [movimientos, mes]);

  const totalVar = gastosVarMes.reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);
  const totalFijos = Object.values(costos).filter(c => !c.esAmortizacion).reduce((a, c) => a + (parseFloat(c.monto) || 0), 0);
  const totalAmort = Object.values(costos).filter(c => c.esAmortizacion).reduce((a, c) => a + (parseFloat(c.monto) || 0), 0);
  const totalPagado = Object.values(costos).filter(c => c.pagado && !c.esAmortizacion).reduce((a, c) => a + (parseFloat(c.monto) || 0), 0);
  const totalProyectado = totalFijos - totalPagado;
  const prorrateo = totalFijos / 4.33;

  const mesLabel = mes.split('-')[1] + '/' + mes.split('-')[0];

  return (
    <div>
      {/* Summary */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { label: 'Total costos fijos', val: fmtM(totalFijos), color: 'var(--red)', sub: 'Pagados + proyectados' },
          { label: 'Ya pagados', val: fmtM(totalPagado), color: 'var(--grn)', sub: `${Object.values(costos).filter(c=>c.pagado&&!c.esAmortizacion).length} costos` },
          { label: 'Proyectados (no pagados)', val: fmtM(totalProyectado), color: 'var(--yel)', sub: 'Aún pendientes' },
          { label: 'Amortizaciones', val: fmtM(totalAmort), color: 'var(--tx2)', sub: 'No impacta flujo' },
          { label: 'Prorrateo semanal', val: fmtM(prorrateo), color: 'var(--tx)', sub: 'Pagados + proyectados ÷ 4.33' },
          { label: 'Gastos variables', val: fmtM(totalVar), color: 'var(--red)', sub: `${gastosVarMes.length} registros` },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '12px 14px', flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Courier, monospace', color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <SectionHeader title={`Costos fijos — ${mesLabel} (Pagados + proyectados = total a prorratear)`} />
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden', marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surf2)' }}>
              {['Costo', 'Monto', 'Fecha vencimiento', 'Estado', 'Calendario', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '9px 13px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.values(costos).map(costo => {
              const vencido = !costo.pagado && costo.fecha && costo.fecha < hoy;
              const proximo = !costo.pagado && costo.fecha && costo.fecha >= hoy && costo.fecha <= new Date(Date.now() + 5*86400000).toISOString().slice(0,10);
              return (
                <tr key={costo.nombre} style={{ borderBottom: '1px solid var(--border)', background: vencido ? 'var(--yel-l)' : '' }}>
                  <td style={{ padding: '9px 13px', fontWeight: 600 }}>
                    {costo.nombre}
                    {vencido && <span style={{ marginLeft: 6, color: 'var(--yel)', fontWeight: 900, fontSize: 14 }} title="Vencido sin registrar pago">!</span>}
                    {proximo && <span style={{ marginLeft: 6, color: 'var(--ora)', fontWeight: 900, fontSize: 14 }} title="Vence pronto">⚠</span>}
                  </td>
                  <td style={{ padding: '9px 13px' }}>
                    <input type="number" value={costo.monto || ''} placeholder="0"
                      onChange={e => updateCosto(costo.nombre, 'monto', parseFloat(e.target.value) || 0)}
                      style={{ border: '1px solid var(--border2)', borderRadius: 5, padding: '4px 8px', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 12, width: 120, background: 'var(--surf)', color: 'var(--tx)' }} />
                  </td>
                  <td style={{ padding: '9px 13px' }}>
                    {costo.pagado ? (
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--grn)', fontWeight: 600 }}>Pagado {fmtDate(costo.fechaPago)}</div>
                        {costo.medioPago && <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{costo.medioPago === 'efectivo' ? 'Efectivo' : 'Transferencia'}</div>}
                        {costo.comprobante && <a href={costo.comprobante} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: 'var(--blu-m)', textDecoration: 'none', fontWeight: 600 }}>📎 Ver comprobante</a>}
                        {costo.fechaSiguiente && (
                          <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>
                            Próx: {fmtDate(costo.fechaSiguiente)}
                            <button onClick={() => resetPago(costo.nombre)} style={{ marginLeft: 6, fontSize: 10, color: 'var(--blu-m)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>→ Cargar próximo mes</button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <input type="date" value={costo.fecha || ''}
                        onChange={e => updateCosto(costo.nombre, 'fecha', e.target.value)}
                        style={{ border: `1px solid ${vencido ? 'var(--yel)' : 'var(--border2)'}`, borderRadius: 5, padding: '4px 6px', fontSize: 11, background: vencido ? 'var(--yel-l)' : 'var(--surf)', color: 'var(--tx)' }} />
                    )}
                  </td>
                  <td style={{ padding: '9px 13px' }}>
                    {costo.pagado
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: 'var(--grn-l)', color: 'var(--grn)' }}>✓ Pagado</span>
                      : vencido
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: 'var(--yel-l)', color: 'var(--yel)' }}>! Vencido</span>
                        : proximo
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: 'var(--ora-l)', color: 'var(--ora)' }}>⚠ Próximo</span>
                          : <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: 'var(--surf3)', color: 'var(--tx2)' }}>
                              {costo.fecha ? 'Proyectado' : 'Sin fecha'}
                            </span>
                    }
                  </td>
                  <td style={{ padding: '9px 13px' }}>
                    {!costo.pagado && costo.fecha && (
                      <a href={gcalUrl(`Pago ${costo.nombre}`, costo.fecha, `Monto estimado: ${fmtM(costo.monto)}`)}
                        target="_blank" rel="noreferrer"
                        style={{ fontSize: 10, color: 'var(--blu-m)', textDecoration: 'none', padding: '2px 6px', background: 'var(--blu-l)', borderRadius: 4, fontWeight: 600 }}>
                        📅 Calendar
                      </a>
                    )}
                  </td>
                  <td style={{ padding: '9px 13px' }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      {!costo.pagado && !costo.esAmortizacion && (
                        <Btn size="sm" variant="grn" onClick={() => openPay(costo)}>Pagar</Btn>
                      )}
                      <Btn size="sm" variant="red" onClick={() => deleteCosto(costo.nombre)}>×</Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add new costo */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
        <input value={newCostoName} onChange={e => setNewCostoName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addCosto()}
          placeholder="Nombre del nuevo costo fijo..."
          style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)', outline: 'none' }} />
        <Btn variant="grn" onClick={addCosto}>+ Agregar costo</Btn>
      </div>

      {/* Gastos variables */}
      <SectionHeader title={`Gastos variables — ${mesLabel}`} />
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
        {gastosVarMes.length === 0
          ? <div style={{ textAlign: 'center', padding: 30, color: 'var(--tx3)', fontSize: 12 }}>Sin gastos variables este mes. Cargalos desde "Gastos variables".</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--surf2)' }}>
                  {['Fecha','Concepto','Monto','Medio'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '9px 13px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gastosVarMes.sort((a,b) => b.fecha.localeCompare(a.fecha)).map(m => (
                  <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontSize: 11, color: 'var(--tx3)' }}>{fmtDate(m.fecha)}</td>
                    <td style={{ padding: '9px 13px' }}>{m.desc || m.concepto || '—'}</td>
                    <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(m.monto)}</td>
                    <td style={{ padding: '9px 13px', fontSize: 11, color: 'var(--tx3)' }}>{m.medio === 'efectivo' ? 'Efectivo' : 'Transferencia'}</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--surf2)' }}>
                  <td colSpan={2} style={{ padding: '9px 13px', fontWeight: 700 }}>Total variables</td>
                  <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>{fmtM(totalVar)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
      </div>

      {/* Pay modal */}
      {payModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}
          onClick={e => { if (e.target === e.currentTarget) setPayModal(null); }}>
          <div style={{ background: 'var(--surf)', borderRadius: 'var(--rl)', border: '1px solid var(--border2)', padding: 24, width: 400, boxShadow: '0 8px 40px rgba(0,0,0,.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>Pagar — {payModal.nombre}</h3>
              <button onClick={() => setPayModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 22 }}>×</button>
            </div>
            <div style={{ background: 'var(--surf2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, fontFamily: 'Courier, monospace', fontWeight: 700 }}>
              {fmtM(payModal.monto)}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Fecha de pago</label>
              <input type="date" value={payFecha} onChange={e => setPayFecha(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Medio de pago</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['transferencia','Transferencia'],['efectivo','Efectivo']].map(([val, label]) => (
                  <button key={val} onClick={() => setPayMedio(val)}
                    style={{ flex: 1, padding: 12, borderRadius: 8, border: `2px solid ${payMedio === val ? 'var(--grn)' : 'var(--border2)'}`, background: payMedio === val ? 'var(--grn-l)' : 'var(--surf2)', color: payMedio === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Link comprobante (Google Drive) — opcional</label>
              <input type="url" value={payComprobante} onChange={e => setPayComprobante(e.target.value)}
                placeholder="https://drive.google.com/..."
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 12, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
            </div>
            <div style={{ background: 'var(--blu-l)', borderRadius: 8, padding: '9px 12px', marginBottom: 16, fontSize: 11, color: 'var(--blu-m)' }}>
              Al confirmar, se registra el pago y se propone la fecha del próximo mes automáticamente.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setPayModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--surf2)', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--tx)' }}>Cancelar</button>
              <button onClick={confirmPay} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--grn)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Confirmar pago</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
